import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { getTheme } from '../theme';
import { useAuth } from './AuthContext';
import { apiClient } from '../services/apiClient';

const THEME_STORAGE_KEY = 'sinaptya-theme';

type ThemeContextType = {
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
  toggleMode: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};

interface ThemeModeProviderProps {
  children: ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [mode, setModeState] = useState<PaletteMode>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as PaletteMode | null;
    if (stored === 'light' || stored === 'dark') return stored;
    return 'light';
  });
  const [initialized, setInitialized] = useState(false);

  // Cargar preferencia del usuario al iniciar sesión
  useEffect(() => {
    if (isAuthenticated && user?.preferences?.theme) {
      const themePref = user.preferences.theme as PaletteMode;
      if (themePref === 'light' || themePref === 'dark') {
        setModeState(themePref);
        localStorage.setItem(THEME_STORAGE_KEY, themePref);
      }
    }
    setInitialized(true);
  }, [isAuthenticated, user?.preferences?.theme]);

  const { updateUser } = useAuth();

  const setMode = useCallback(async (newMode: PaletteMode) => {
    setModeState(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
    if (isAuthenticated) {
      try {
        const res = await apiClient.put('/auth/preferences', { theme: newMode });
        if (res.data?.user) updateUser(res.data.user);
      } catch (err) {
        console.error('Error guardando preferencia de tema:', err);
      }
    }
  }, [isAuthenticated, updateUser]);

  const toggleMode = useCallback(() => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
  }, [mode, setMode]);

  const value: ThemeContextType = {
    mode,
    setMode,
    toggleMode,
  };

  // Evitar flash: no renderizar hasta tener valor inicial
  const theme = getTheme(initialized ? mode : 'light');

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
